import type { ZeroRoomAutoLinkRunPhase } from '../verify-auto-link/v2/auto-link-bootstrap-context.js';

export type AccountStudio<TStudio> = {
  readonly id: string;
  readonly value: TStudio;
};

export type AccountStudioInventory<TStudio> =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly studios: readonly AccountStudio<TStudio>[] }
  | {
      readonly status: 'error';
      readonly reason: 'load_failed' | 'malformed_room_identity';
    };

export type AccountIdentity =
  | { readonly status: 'resolving' }
  | { readonly status: 'linked'; readonly agentId: string }
  | { readonly status: 'unlinked' }
  | { readonly status: 'error'; readonly cause: string };

export type AccountBootstrapRoutingMode = 'electron_iris' | 'electron_legacy' | 'browser_legacy';
export type AccountBootstrapPickerMode = 'bridge_setup' | 'studio_access';

export type AccountBootstrapInput<TStudio> = {
  readonly inventory: AccountStudioInventory<TStudio>;
  readonly identity: AccountIdentity;
  readonly linkedStudioId: string | undefined;
  readonly preferredStudioId: string | undefined;
  readonly rememberStudio: boolean;
  readonly routingMode: AccountBootstrapRoutingMode;
};

export type AccountZeroStudioDecision =
  | { readonly kind: 'request_zero_auto_link' }
  | { readonly kind: 'bootstrap_default_studio' }
  | { readonly kind: 'open_agent_setup' };

export type AccountBootstrapDecision<TStudio> =
  | { readonly kind: 'wait_for_identity' }
  | { readonly kind: 'identity_error'; readonly cause: string }
  | { readonly kind: 'wait_for_inventory' }
  | {
      readonly kind: 'inventory_error';
      readonly reason: 'load_failed' | 'malformed_room_identity';
    }
  | AccountZeroStudioDecision
  | { readonly kind: 'access_empty' }
  | { readonly kind: 'enter_studio'; readonly studio: AccountStudio<TStudio> }
  | { readonly kind: 'choose_studio'; readonly mode: AccountBootstrapPickerMode };

export type AccountBootstrapSession = {
  readonly accountId: string;
  readonly generation: number;
};

export type FencedAccountBootstrapDecision<TStudio> =
  | AccountBootstrapDecision<TStudio>
  | { readonly kind: 'stale' };

class UnexpectedAccountBootstrapStateError extends Error {
  constructor(value: never) {
    super(`Unexpected account bootstrap state: ${JSON.stringify(value)}`);
    this.name = 'UnexpectedAccountBootstrapStateError';
  }
}

const assertNever = (value: never): never => {
  throw new UnexpectedAccountBootstrapStateError(value);
};

/** Parse the authoritative API result once so malformed rooms cannot masquerade as an empty list. */
export const createAccountStudioInventory = <TStudio>(
  studios: readonly TStudio[],
  getStudioId: (studio: TStudio) => string | undefined
): AccountStudioInventory<TStudio> => {
  // Mutable only as the parser accumulator; the exported inventory is readonly.
  const parsed: AccountStudio<TStudio>[] = [];

  for (const studio of studios) {
    const id = getStudioId(studio);
    if (id === undefined || id === '') {
      return { status: 'error', reason: 'malformed_room_identity' };
    }
    parsed.push({ id, value: studio });
  }

  return { status: 'loaded', studios: parsed };
};

/** Inventory cardinality is authoritative; identity and preferences cannot outrank it. */
export const decideAccountBootstrap = <TStudio>(
  input: AccountBootstrapInput<TStudio>
): AccountBootstrapDecision<TStudio> => {
  if (input.routingMode === 'electron_iris') {
    // Account-first intent is impossible to infer until the local Bridge is
    // settled: linked means direct entry, while unlinked means setup/linking.
    switch (input.identity.status) {
      case 'resolving':
        return { kind: 'wait_for_identity' };
      case 'error':
        return { kind: 'identity_error', cause: input.identity.cause };
      case 'linked':
      case 'unlinked':
        break;
      default:
        return assertNever(input.identity);
    }
  }

  switch (input.inventory.status) {
    case 'loading':
      return { kind: 'wait_for_inventory' };
    case 'error':
      return { kind: 'inventory_error', reason: input.inventory.reason };
    case 'loaded': {
      const [soleStudio, secondStudio] = input.inventory.studios;
      if (soleStudio === undefined) {
        switch (input.routingMode) {
          case 'electron_iris': {
            return input.identity.status === 'linked'
              ? { kind: 'access_empty' }
              : { kind: 'request_zero_auto_link' };
          }
          case 'electron_legacy':
            return { kind: 'bootstrap_default_studio' };
          case 'browser_legacy':
            return { kind: 'open_agent_setup' };
          default:
            return assertNever(input.routingMode);
        }
      }
      if (input.routingMode === 'electron_iris') {
        // Desktop launch is identity-owned: a linked Bridge has no Studio-selection step.
        if (input.identity.status === 'linked') {
          const linkedStudio = input.inventory.studios.find(
            (studio) => studio.id === input.linkedStudioId
          );
          return linkedStudio === undefined
            ? { kind: 'access_empty' }
            : { kind: 'enter_studio', studio: linkedStudio };
        }
        return { kind: 'choose_studio', mode: 'bridge_setup' };
      }
      if (secondStudio === undefined) {
        return { kind: 'enter_studio', studio: soleStudio };
      }

      // Browser and Hive retain the historical fast paths; the Iris Electron
      // branch returned above before preferences could bypass its picker.
      const linkedStudio = input.inventory.studios.find(
        (studio) => studio.id === input.linkedStudioId
      );
      if (linkedStudio !== undefined) {
        return { kind: 'enter_studio', studio: linkedStudio };
      }

      const preferredStudio = input.rememberStudio
        ? input.inventory.studios.find((studio) => studio.id === input.preferredStudioId)
        : undefined;
      if (preferredStudio !== undefined) {
        return { kind: 'enter_studio', studio: preferredStudio };
      }

      // ENG-4483 (ITB finding #001): this picker iteration is the Desktop
      // Bridge Setup flow and only Iris Electron reaches it (returned above).
      // Web has no local Bridge, and Hive keeps its legacy boot, so both fall
      // back to the previously shipped behavior: enter the first Studio.
      // The /select-studio route itself stays mounted for the later
      // organization-enabled picker; nothing routes there from here.
      return { kind: 'enter_studio', studio: soleStudio };
    }
    default:
      return assertNever(input.inventory);
  }
};

/**
 * A zero-Studio Auto Link run already in flight owns the account's first
 * Studio, so a new bootstrap pass joins it instead of re-deciding from the
 * inventory that run is changing: a created-but-unlinked Studio would read as
 * the picker, and a just-linked Bridge on stale inventory as access_empty.
 *
 * A run that `succeeded` keeps steering every pass for the rest of Root's
 * mount (the guard wraps only the index route): any other decision comes from
 * an inventory or identity read that has not caught up with the link, and
 * joining opens the new Studio instead of the picker or an unlink.
 *
 * A run that committed the link and then failed (`link_committed`) is joined
 * only by access_empty, so a stale read never unlinks the Bridge this session
 * linked; the pass still gets the run's failure. Other decisions are the
 * pass's own, since a fresh read may show a usable Studio.
 *
 * The exception is access_empty while the run is still `checking`: it has not
 * linked anything yet, so the linked Bridge belongs to another account and
 * Root's cross-account unlink recovery must run. Joining would instead wait for
 * the run to end in `cross_account_link`, which Root reports as "No rooms".
 */
export const joinInFlightZeroStudioAutoLink = <TStudio>(
  decision: FencedAccountBootstrapDecision<TStudio>,
  runPhase: ZeroRoomAutoLinkRunPhase
): FencedAccountBootstrapDecision<TStudio> => {
  if (runPhase === 'none' || decision.kind === 'stale') return decision;
  if (decision.kind === 'access_empty') {
    return runPhase === 'checking' ? decision : { kind: 'request_zero_auto_link' };
  }
  return runPhase === 'link_committed' ? decision : { kind: 'request_zero_auto_link' };
};

/** Generation fence used by Root to reject late account/session completions. */
export class AccountBootstrapCoordinator {
  private generation = 0;
  private currentAccountId: string | undefined;

  begin(accountId: string): AccountBootstrapSession {
    this.generation += 1;
    this.currentAccountId = accountId;
    return { accountId, generation: this.generation };
  }

  isCurrent(session: AccountBootstrapSession): boolean {
    return session.accountId === this.currentAccountId && session.generation === this.generation;
  }

  decide<TStudio>(
    session: AccountBootstrapSession,
    input: AccountBootstrapInput<TStudio>
  ): FencedAccountBootstrapDecision<TStudio> {
    if (!this.isCurrent(session)) {
      return { kind: 'stale' };
    }
    return decideAccountBootstrap(input);
  }
}

/** The bridge watchdog is meaningful only while the settled zero-room branch is linking. */
export const shouldEnableBootstrapWatchdog = <TStudio>(
  decision: FencedAccountBootstrapDecision<TStudio>
): boolean => {
  switch (decision.kind) {
    case 'request_zero_auto_link':
      return true;
    case 'wait_for_identity':
    case 'identity_error':
    case 'wait_for_inventory':
    case 'inventory_error':
    case 'bootstrap_default_studio':
    case 'open_agent_setup':
    case 'access_empty':
    case 'enter_studio':
    case 'choose_studio':
    case 'stale':
      return false;
    default:
      return assertNever(decision);
  }
};
